import cn from 'classnames';
import { useContext } from 'react';

import { CustomStylesCtx } from '@webapp/common/components/custom-styles';
import { CssUiComponent } from '@webapp/common/components/survey-custom';
import { setHtml } from '@webapp/common/lib/ui';

import type { MultiSelectProps, SelectOption, SelectProps } from './lib';

export const getStyles = ({ select, text, view, maxWidth }): Record<string, any> => {
    const isTransparent = view === 'transparent';

    return {
        menu: (base: CSSProperties) => ({
            ...base,
            ...(maxWidth ? { width: 'max-content', minWidth: '100%' } : {}),
            zIndex: 700000,
            borderRadius: '10px',
        }),
        menuList: (base: CSSProperties) => ({ ...base, padding: '0 4px' }),
        menuPortal: (base: CSSProperties) => ({ ...base, zIndex: 700000 }),
        control: (base: CSSProperties) => {
            const result = {
                ...base,
                minHeight: '34px',
                backgroundColor: select.root?.backgroundColor || 'var(--c-border)',
                borderColor: select.root?.borderColor || 'var(--c-border)',
                borderRadius: '8px',
                boxShadow: 'none',
                ':hover': select.root?.borderColor
                    ? {
                          borderColor: `${select.root?.['--c-border-focus']} !important`,
                      }
                    : {},
            };

            if (isTransparent) {
                result.backgroundColor = 'transparent';
                result.border = 'none';
            } else if (view === 'white') {
                result.backgroundColor = '#FFF';
                result.border = '#FFF';
            }

            return result;
        },
        valueContainer: (base: CSSProperties) => ({
            ...base,
            color: text.color || base.color,
            paddingTop: 0,
            paddingBottom: 0,
            paddingRight: '2px',
        }),
        input: (base: CSSProperties) => ({
            ...base,
            color: text.color || base.color,
        }),
        container: (base: CSSProperties) => ({ ...base, ...text }),
        dropdownIndicator: (base: CSSProperties) => ({
            ...base,
            padding: isTransparent ? '0' : '6px',
            color: select.arrow?.color ? `${select.arrow?.color} !important` : base.color,
            ':hover': {
                color: base.color,
            },
        }),
        indicatorSeparator: () => ({ display: 'none' }),
        singleValue: (base: CSSProperties) => ({
            ...base,
            color: text.color || base.color,
        }),
        multiValueLabel: (base: CSSProperties) => ({
            ...base,
            color: text.color || base.color,
        }),
        multiValue: (base: CSSProperties) => ({
            ...base,
            borderRadius: '6px',
        }),
        option: (base: CSSProperties, state: { isFocused?: boolean; isSelected?: boolean }) => {
            const item = state.isFocused || state.isSelected ? { ...select.item, ...select.activeItem } : select.item;
            const activeBackgroundColor = select.activeItem?.backgroundColor;

            return {
                ...base,
                color: item?.color || base.color,
                backgroundColor: item?.backgroundColor || base.backgroundColor,
                ...(activeBackgroundColor ? { ':active': { backgroundColor: activeBackgroundColor } } : {}),
                wordBreak: 'break-word',
                borderRadius: '6px',
                margin: '4px 0',
            };
        },
        multiValueRemove: (base: CSSProperties) => ({
            ...base,
            cursor: 'pointer',
        }),
        clearIndicator: (base: CSSProperties) => ({
            ...base,
            paddingTop: '6px',
            paddingBottom: '6px',
            cursor: 'pointer',
        }),
    };
};

export const useGetCommonProps = ({
    id,
    options,
    isOpen,
    disabled,
    placeholder = 'Выбрать из списка',
    className,
    classNames,
    searchable = false,
    clearable,
    menuPosition,
    view,
    stretchMenuToLongestOption,
    classNamePrefix,
}: SelectProps | MultiSelectProps): Record<string, any> => {
    const { select, text } = useContext(CustomStylesCtx);
    const styles = getStyles({ select, text, view, maxWidth: stretchMenuToLongestOption });

    return {
        id,
        options,
        styles,
        placeholder,
        menuPosition: menuPosition || 'fixed',
        noOptionsMessage: () => '',
        defaultMenuIsOpen: isOpen,
        isDisabled: disabled,
        className: cn(CssUiComponent.SELECT, className),
        classNames: {
            control: () => classNames?.control,
            option: () => classNames?.option,
            placeholder: () => classNames?.placeholder,
        },
        classNamePrefix,
        isSearchable: searchable,
        isClearable: clearable,
        menuPortalTarget: typeof window !== 'undefined' ? document.body : null,
        formatOptionLabel: (option: SelectOption) => {
            return option.isHtml ? <div {...setHtml(option.label)} /> : option.label;
        },
        filterOption: (option: SelectOption, inputValue: string) => {
            if (typeof option.label === 'string') {
                return option.label.toLowerCase().includes(inputValue.toLowerCase());
            }

            return true;
        },
    };
};
